import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { IconSend, IconChevron } from '@components/icons';

const CATEGORY_KEYS = ['general', 'payroll', 'contract', 'leave', 'documents', 'it'];
const STATUS_CLS = {
  open:     'bg-amber-100 text-amber-800',
  answered: 'bg-green-100 text-green-800',
  closed:   'bg-gray-200 text-gray-600',
};

export default function Tickets() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const draft = location.state?.draft;
  const [view,    setView]    = useState(draft ? 'new' : 'list'); // list | new | <id>
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fmt = (d) => new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  const load = () => {
    setLoading(true);
    api.get('/tickets/me').then(({ data }) => setTickets(data.tickets)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <>
      <PageHeader title={t('tickets.title')} subtitle={t('tickets.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        {view === 'new' ? (
          <NewTicket draft={draft} onCancel={() => setView('list')} onCreated={(id) => { load(); setView(id); }} />
        ) : view === 'list' ? (
          <>
            <button onClick={() => setView('new')} className="btn-primary w-full mb-4">{t('tickets.new')}</button>
            {loading ? (
              <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
            ) : tickets.length === 0 ? (
              <div className="card p-8 text-center text-gray-500 text-sm">{t('tickets.empty')}</div>
            ) : (
              <ul className="space-y-2">
                {tickets.map((tk) => (
                  <li key={tk.id}>
                    <button onClick={() => setView(tk.id)} className="w-full text-left card p-4 hover:border-glk-green/40 transition-colors cursor-pointer flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-glk-dark truncate">{tk.subject}</span>
                          <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_CLS[tk.status] || STATUS_CLS.open}`}>{t(`tickets.status.${tk.status}`)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{t(`tickets.categories.${tk.category}`)} · {fmt(tk.updated_at)}</p>
                      </div>
                      <IconChevron size={18} className="text-gray-300 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <Thread id={view} onBack={() => { load(); setView('list'); }} />
        )}
      </div>
    </>
  );
}

function NewTicket({ draft, onCancel, onCreated }) {
  const { t } = useI18n();
  const [category, setCategory] = useState('general');
  const [subject,  setSubject]  = useState('');
  const [body,     setBody]     = useState(draft || '');
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!subject.trim() || !body.trim()) { setError(t('tickets.errFill')); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/tickets', { category, subject, body });
      onCreated(data.ticket.id);
    } catch (err) {
      setError(err.response?.data?.message || t('tickets.errSend'));
    } finally { setBusy(false); }
  };

  return (
    <div className="card p-5">
      <h2 className="text-sm font-semibold text-glk-dark mb-4">{t('tickets.newTitle')}</h2>
      {error && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label">{t('tickets.subject')}</label>
          <select className="input mb-2" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORY_KEYS.map((k) => <option key={k} value={k}>{t(`tickets.categories.${k}`)}</option>)}
          </select>
          <input className="input" value={subject} maxLength={200} placeholder={t('tickets.subjectPlaceholder')}
                 onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <label className="label">{t('tickets.message')}</label>
          <textarea className="input resize-none" rows={5} value={body} maxLength={4000}
                    placeholder={t('tickets.messagePlaceholder')} onChange={(e) => setBody(e.target.value)} />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="btn-ghost flex-1">{t('tickets.cancel')}</button>
          <motion.button type="submit" disabled={busy} whileTap={{ scale: 0.98 }} className="btn-primary flex-1">
            {busy ? t('tickets.sending') : t('tickets.send')}
          </motion.button>
        </div>
      </form>
    </div>
  );
}

function Thread({ id, onBack }) {
  const { t, lang } = useI18n();
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');

  const fmt = (d) => new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  const load = () => {
    setLoading(true);
    api.get(`/tickets/${id}`).then(({ data }) => setTicket(data.ticket)).catch(() => setError(t('tickets.loadError'))).finally(() => setLoading(false));
  };
  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true); setError('');
    try {
      await api.post(`/tickets/${id}/messages`, { body: reply });
      setReply('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || t('tickets.errSend'));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>;
  if (!ticket)  return <div className="card p-6 text-red-600 text-sm">{error}</div>;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-glk-green hover:underline mb-3">{t('tickets.back')}</button>
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-base font-semibold text-glk-dark flex-1">{ticket.subject}</h2>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_CLS[ticket.status] || STATUS_CLS.open}`}>{t(`tickets.status.${ticket.status}`)}</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">{t(`tickets.categories.${ticket.category}`)}</p>

        <div className="space-y-3">
          {ticket.messages.map((m) => (
            <div key={m.id} className={`flex ${m.author_role === 'staff' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                m.author_role === 'staff' ? 'bg-glk-green text-white rounded-br-sm' : 'bg-gray-100 text-glk-dark rounded-bl-sm'
              }`}>
                <p className="text-[10px] opacity-70 mb-0.5">{m.author_role === 'staff' ? t('tickets.me') : `${t('tickets.hr')} · ${m.author_name}`} · {fmt(m.created_at)}</p>
                {m.body}
              </div>
            </div>
          ))}
        </div>

        {ticket.status !== 'closed' && (
          <form onSubmit={send} className="mt-4 flex items-end gap-2 border-t border-gray-100 pt-3">
            <textarea className="input flex-1 resize-none" rows={2} value={reply}
                      placeholder={t('tickets.replyPlaceholder')} onChange={(e) => setReply(e.target.value)} />
            <button type="submit" disabled={busy || !reply.trim()} className="btn-primary !px-3.5 shrink-0" aria-label={t('tickets.send')}>
              <IconSend size={18} />
            </button>
          </form>
        )}
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    </div>
  );
}
